import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaFilter, FaSync, FaEdit, FaFlag, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import orderService from '../../services/orderService';
import { useAccessToken } from '../../hooks';
import { getOrderStatus, ORDER_STATUS_COLORS, ORDER_STATUS_LABELS, PaginationHelper } from '../../types/orders.types';
import EditOrderModal from '../../components/ui/EditOrderModal';
import styles from './ViewOrders.module.css';

/**
 * View Orders Component
 * 
 * Displays a paginated, searchable, filterable table of all orders
 * with derived status indicators and action buttons
 */
const ViewOrders = () => {
    const { getBackendAccessToken } = useAccessToken();
    
    // Data state
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    
    // Filter/Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [reviewFilter, setReviewFilter] = useState('All');
    const [sortBy, setSortBy] = useState('orderDate');
    const [sortDirection, setSortDirection] = useState('desc');
    
    // UI state
    const [showFilters, setShowFilters] = useState(false);
    const [authError, setAuthError] = useState(false);
    
    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);

    /**
     * Debounce search term to avoid excessive API calls
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on new search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    /**
     * Load orders whenever pagination or filter criteria change
     */
    useEffect(() => {
        // Load orders when dependencies change
        if (!authError) {
            loadOrders();
        }
    }, [currentPage, pageSize, debouncedSearchTerm, statusFilter, reviewFilter, sortBy, sortDirection]);

    /**
     * Load orders from API with pagination
     */
    const loadOrders = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get access token
            const accessToken = await getBackendAccessToken();

            // Build pagination params
            const sortString = PaginationHelper.buildSortString(sortBy, sortDirection);
            const params = PaginationHelper.buildPageParams(currentPage, pageSize, sortBy, sortDirection);

            let response;

            // Apply filters/search
            if (debouncedSearchTerm) {
                // Search takes precedence - with fallback to client-side filtering
                try {
                    response = await orderService.searchOrdersPaged(accessToken, debouncedSearchTerm, params);
                } catch (searchError) {
                    console.warn('Backend search failed, falling back to client-side filtering:', searchError);
                    // Fallback: Get all orders and filter client-side
                    const allOrdersResponse = await orderService.getAllOrdersPaged(accessToken, {
                        ...params,
                        size: 1000 // Get more orders for client-side filtering
                    });
                    
                    // Client-side search filter
                    const searchLower = debouncedSearchTerm.toLowerCase();
                    const filteredOrders = (allOrdersResponse.content || []).filter(order => 
                        order.orderId?.toString().toLowerCase().includes(searchLower) ||
                        order.clientName?.toLowerCase().includes(searchLower) ||
                        order.clientCode?.toLowerCase().includes(searchLower)
                    );
                    
                    // Manual pagination of filtered results
                    const startIndex = currentPage * pageSize;
                    const endIndex = startIndex + pageSize;
                    const paginatedOrders = filteredOrders.slice(startIndex, endIndex);
                    
                    response = {
                        content: paginatedOrders,
                        totalPages: Math.ceil(filteredOrders.length / pageSize),
                        totalElements: filteredOrders.length,
                        number: currentPage,
                        size: pageSize
                    };
                }
            } else if (reviewFilter === 'Flagged') {
                // Filter by review flag
                response = await orderService.getOrdersNeedingReview(accessToken, params);
            } else if (statusFilter === 'Pending') {
                // Not processed and not completed
                response = await orderService.getOrdersByProcessed(accessToken, false, params);
            } else if (statusFilter === 'InProgress') {
                // Processed but not completed
                response = await orderService.getOrdersByProcessed(accessToken, true, params);
                // Note: This will include completed orders. For true InProgress, we'd need backend support
                // or client-side filtering of the response
            } else if (statusFilter === 'Completed') {
                // Completed
                response = await orderService.getOrdersByCompleted(accessToken, true, params);
            } else {
                // All orders
                response = await orderService.getAllOrdersPaged(accessToken, params);
            }

            // Update state with paginated response
            setOrders(response.content || []);
            setTotalPages(response.totalPages || 0);
            setTotalElements(response.totalElements || 0);
            setAuthError(false); // Clear auth error on success

        } catch (err) {
            console.error('Failed to load orders:', err);
            
            // Check if this is an authentication error
            if (err.message && (err.message.includes('401') || err.message.includes('Unauthorized'))) {
                setAuthError(true);
                setError('Authentication failed. Please try logging in again.');
            } else {
                setError(err.message || 'Failed to load orders');
            }
            
            setOrders([]);
            setTotalPages(0);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    };


    /**
     * Handle page navigation
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    /**
     * Handle page size change
     */
    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(0); // Reset to first page
    };

    /**
     * Handle sort column click
     */
    const handleSort = (column) => {
        if (sortBy === column) {
            // Toggle direction
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New column, default to ascending
            setSortBy(column);
            setSortDirection('asc');
        }
        setCurrentPage(0); // Reset to first page
    };

    /**
     * Clear all filters
     */
    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('All');
        setReviewFilter('All');
        setCurrentPage(0);
    };


    /**
     * Format date for display
     */
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    /**
     * Get status badge class
     */
    const getStatusBadgeClass = (order) => {
        const status = getOrderStatus(order);
        const color = ORDER_STATUS_COLORS[status];
        return `${styles.statusBadge} ${styles[`badge-${color}`]}`;
    };

    return (
        <div className={styles.viewOrders}>
            {/* Header */}
            <div className={styles.header}>
                <h2>View Orders</h2>
                <button 
                    className={styles.refreshButton}
                    onClick={() => {
                        setAuthError(false);
                        loadOrders();
                    }}
                    disabled={loading}
                >
                    <FaSync className={loading ? styles.spinning : ''} />
                    Refresh
                </button>
            </div>

            {/* Search and Filters */}
            <div className={styles.controls}>
                {/* Search Bar */}
                <div className={styles.searchBar}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search by Order ID, Client Name, or Client Code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>

                {/* Filter Toggle */}
                <button 
                    className={`${styles.filterButton} ${showFilters ? styles.active : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <FaFilter />
                    Filters
                </button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <div className={styles.filterPanel}>
                    <div className={styles.filterGroup}>
                        <label>Status:</label>
                        <select 
                            value={statusFilter} 
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className={styles.filterSelect}
                        >
                            <option value="All">All Statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="InProgress">In Progress</option>
                            <option value="Completed">Completed</option>
                        </select>
                    </div>

                    <div className={styles.filterGroup}>
                        <label>Review Flag:</label>
                        <select 
                            value={reviewFilter} 
                            onChange={(e) => setReviewFilter(e.target.value)}
                            className={styles.filterSelect}
                        >
                            <option value="All">All</option>
                            <option value="Flagged">Flagged for Review</option>
                            <option value="Not Flagged">Not Flagged</option>
                        </select>
                    </div>

                    <div className={styles.filterGroup}>
                        <label>Sort By:</label>
                        <select 
                            value={sortBy} 
                            onChange={(e) => setSortBy(e.target.value)}
                            className={styles.filterSelect}
                        >
                            <option value="orderDate">Order Date</option>
                            <option value="orderId">Order ID</option>
                            <option value="clientName">Client Name</option>
                            <option value="processedDate">Processed Date</option>
                            <option value="completedDate">Completed Date</option>
                            <option value="status">Status</option>
                        </select>
                    </div>

                    <div className={styles.filterGroup}>
                        <label>Direction:</label>
                        <select 
                            value={sortDirection} 
                            onChange={(e) => setSortDirection(e.target.value)}
                            className={styles.filterSelect}
                        >
                            <option value="asc">Ascending</option>
                            <option value="desc">Descending</option>
                        </select>
                    </div>

                    <button 
                        className={styles.clearFiltersButton}
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </button>
                </div>
            )}

            {/* Combined Pagination and Results Bar */}
            <div className={styles.paginationBar}>
                {/* Left: Results Summary */}
                <div className={styles.resultsSummary}>
                    Showing {orders.length > 0 ? (currentPage * pageSize) + 1 : 0} - {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} orders
                </div>

                {/* Center: Pagination Controls */}
                <div className={styles.paginationControls}>
                    <button
                        className={styles.pageButton}
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0 || totalPages <= 1}
                        title="First page"
                    >
                        First
                    </button>
                    
                    <button
                        className={styles.pageButton}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0 || totalPages <= 1}
                        title="Previous page"
                    >
                        <FaChevronLeft /> Previous
                    </button>

                    <span className={styles.pageInfo}>
                        Page {currentPage + 1} of {totalPages || 1}
                    </span>

                    <button
                        className={styles.pageButton}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                        title="Next page"
                    >
                        Next <FaChevronRight />
                    </button>

                    <button
                        className={styles.pageButton}
                        onClick={() => handlePageChange(totalPages - 1)}
                        disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                        title="Last page"
                    >
                        Last
                    </button>
                </div>

                {/* Right: Page Size Selector */}
                <div className={styles.pageSizeSelector}>
                    <label>Items per page:</label>
                    <select value={pageSize} onChange={handlePageSizeChange}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            {/* Orders Table */}
            {loading ? (
                <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Loading orders...</p>
                </div>
            ) : orders.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No orders found</p>
                    {searchTerm || statusFilter !== 'All' || reviewFilter !== 'All' ? (
                        <button onClick={clearFilters} className={styles.clearFiltersLink}>
                            Clear filters to see all orders
                        </button>
                    ) : null}
                </div>
            ) : (
                <>
                    {/* Desktop Table View */}
                    <div className={styles.tableContainer}>
                        <table className={styles.ordersTable}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('orderId')} className={styles.sortable}>
                                        Order ID {sortBy === 'orderId' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th onClick={() => handleSort('orderDate')} className={styles.sortable}>
                                        Order Date {sortBy === 'orderDate' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th onClick={() => handleSort('clientName')} className={styles.sortable}>
                                        Client {sortBy === 'clientName' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th onClick={() => handleSort('status')} className={styles.sortable}>
                                        Status {sortBy === 'status' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th onClick={() => handleSort('processedDate')} className={styles.sortable}>
                                        Processed {sortBy === 'processedDate' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th onClick={() => handleSort('completedDate')} className={styles.sortable}>
                                        Completed {sortBy === 'completedDate' && (sortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => (
                                    <tr key={order.orderId} className={order.reviewFlag ? styles.flagged : ''}>
                                        <td className={styles.orderId}>
                                            {order.orderId}
                                            {order.reviewFlag && (
                                                <FaFlag className={styles.reviewFlagIcon} title="Flagged for review" />
                                            )}
                                        </td>
                                        <td>{formatDate(order.orderDate)}</td>
                                        <td>
                                            <div className={styles.clientCell}>
                                                <span className={styles.clientName}>{order.clientName}</span>
                                                <span className={styles.clientCode}>{order.clientCode}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={getStatusBadgeClass(order)}>
                                                {ORDER_STATUS_LABELS[getOrderStatus(order)]}
                                            </span>
                                        </td>
                                        <td>{formatDate(order.processedDate)}</td>
                                        <td>{formatDate(order.completedDate)}</td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button 
                                                    className={styles.actionButton}
                                                    title="Manage order"
                                                    onClick={() => {
                                                        setEditingOrder(order);
                                                        setEditModalOpen(true);
                                                    }}
                                                >
                                                    <FaEdit />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    {orders.map(order => (
                        <div 
                            key={`mobile-${order.orderId}`} 
                            className={`${styles.orderCard} ${order.reviewFlag ? styles.flagged : ''}`}
                        >
                            <div className={styles.orderCardHeader}>
                                <div className={styles.orderCardId}>
                                    {order.orderId}
                                    {order.reviewFlag && (
                                        <FaFlag className={styles.reviewFlagIcon} title="Flagged for review" />
                                    )}
                                </div>
                                <span className={getStatusBadgeClass(order)}>
                                    {ORDER_STATUS_LABELS[getOrderStatus(order)]}
                                </span>
                            </div>

                            <div className={styles.orderCardDetails}>
                                <div className={styles.orderCardRow}>
                                    <span className={styles.orderCardLabel}>Order Date:</span>
                                    <span className={styles.orderCardValue}>{formatDate(order.orderDate)}</span>
                                </div>
                                
                                <div className={styles.orderCardRow}>
                                    <span className={styles.orderCardLabel}>Client:</span>
                                    <div className={styles.orderCardClient}>
                                        <span className={styles.orderCardClientName}>{order.clientName}</span>
                                        <span className={styles.orderCardClientCode}>{order.clientCode}</span>
                                    </div>
                                </div>

                                <div className={styles.orderCardRow}>
                                    <span className={styles.orderCardLabel}>Processed:</span>
                                    <span className={styles.orderCardValue}>{formatDate(order.processedDate)}</span>
                                </div>

                                <div className={styles.orderCardRow}>
                                    <span className={styles.orderCardLabel}>Completed:</span>
                                    <span className={styles.orderCardValue}>{formatDate(order.completedDate)}</span>
                                </div>
                            </div>

                            <div className={styles.orderCardActions}>
                                <button 
                                    className={styles.actionButton}
                                    title="Manage order"
                                    onClick={() => {
                                        setEditingOrder(order);
                                        setEditModalOpen(true);
                                    }}
                                >
                                    <FaEdit />
                                </button>
                            </div>
                        </div>
                    ))}

                    {/* Pagination Controls - Bottom */}
                    <div className={styles.pagination}>
                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(0)}
                            disabled={currentPage === 0 || totalPages <= 1}
                            title="First page"
                        >
                            First
                        </button>
                        
                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 0 || totalPages <= 1}
                            title="Previous page"
                        >
                            <FaChevronLeft /> Previous
                        </button>

                        <span className={styles.pageInfo}>
                            Page {currentPage + 1} of {totalPages || 1}
                        </span>

                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                            title="Next page"
                        >
                            Next <FaChevronRight />
                        </button>

                        <button
                            className={styles.pageButton}
                            onClick={() => handlePageChange(totalPages - 1)}
                            disabled={currentPage >= totalPages - 1 || totalPages <= 1}
                            title="Last page"
                        >
                            Last
                        </button>
                    </div>
                </>
            )}

            {/* Edit Order Modal */}
            <EditOrderModal
                order={editingOrder}
                isOpen={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setEditingOrder(null);
                }}
                onOrderUpdated={(updatedOrder) => {
                    setEditModalOpen(false);
                    setEditingOrder(null);
                    // Reload orders to reflect changes (including deletions)
                    loadOrders();
                }}
            />
        </div>
    );
};

export default ViewOrders;
